import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Briefcase,
  ChevronDown,
  Home,
  Info,
  Mail,
  MapPin,
  Menu,
  X,
} from "lucide-react";
import { FaFacebook, FaInstagram, FaPhoneAlt, FaTiktok } from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";

const services = [
  "Naturalisation",
  "Titre de séjour",
  "OQTF",
  "Accélération des procédures",
  "Regroupement familial",
  "Dossier bloqué en préfecture",
];

const Navbar = ({
                  home,
                  about,
                  articles,
                  location: meetLocation,
                  contact,
                  facebook,
                  instagram,
                  tiktok,
                  phone,
                  onServiceClick,
                }) => {
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileAboutOpen, setIsMobileAboutOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const closeTimeoutRef = useRef(null);
  const closeAboutTimeoutRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMouseEnter = useCallback((menu) => {
    if (menu === "services") {
      clearTimeout(closeTimeoutRef.current);
      setIsServicesOpen(true);
    } else if (menu === "about") {
      clearTimeout(closeAboutTimeoutRef.current);
      setIsAboutOpen(true);
    }
  }, []);

  const handleMouseLeave = useCallback((menu) => {
    if (menu === "services") {
      closeTimeoutRef.current = setTimeout(() => setIsServicesOpen(false), 100);
    } else if (menu === "about") {
      closeAboutTimeoutRef.current = setTimeout(
          () => setIsAboutOpen(false),
          100,
      );
    }
  }, []);

  const handleServiceClick = (serviceId, e) => {
    if (onServiceClick) {
      e.preventDefault();
      onServiceClick(serviceId);
      setIsServicesOpen(false);
      setIsMobileMenuOpen(false);
    }
  };

  return (
      <div className="Marcellus antialiased border-b border-gray-200 relative overflow-visible" style={{ zIndex: 100 }}>
        <nav
            className={`px-2 transition-all duration-500 ease-in-out ${
                isScrolled
                    ? "transform translate-y-0 opacity-100 navBarStyleScrolling shadow-lg fixed top-0 w-full animate-slideDown"
                    : "transform translate-y-0 opacity-100 navC"
            }`}
            style={{ zIndex: 100 }}
        >
          <div className="mx-auto flex items-center justify-between h-20 lg:justify-around md:justify-around">
            {/* Logo */}
            <div>
              <Link to={home}>
                <img
                    src="/assets/img/logo.png"
                    alt="logo"
                    className="w-40 lg:w-48 md:w-40 object-contain"
                    style={{ top: "-2px" }}
                />
              </Link>
            </div>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-4">
              <Link
                  to={home}
                  className={`font-semibold hover:text-red-600 px-4 ${
                      location.pathname === home ? "active" : "text-blue-900"
                  }`}
              >
                Accueil
              </Link>

              {/* Nos procédures */}
              <div
                  className="relative"
                  onMouseEnter={() => handleMouseEnter("services")}
                  onMouseLeave={() => handleMouseLeave("services")}
              >
                <button className="flex items-center text-blue-900 font-semibold hover:text-red-600 transition-all px-4">
                  Nos procédures
                  <ChevronDown
                      className={`ml-1 h-4 w-4 transition-transform ${
                          isServicesOpen ? "rotate-180" : ""
                      }`}
                  />
                </button>
                <div
                    className={`absolute z-[200] mt-3 rounded-md shadow-lg transition-all origin-top ${
                        isServicesOpen
                            ? "opacity-100 scale-100 visible"
                            : "opacity-0 scale-95 hidden"
                    } ${isScrolled ? "navBarStyleScrollingB" : "navC"}`}
                    style={{ left: "50%", transform: "translateX(-50%)" }}
                >
                  <div className="py-1 text-base">
                    {services.map((service, index) => (
                        <a
                            key={index}
                            href="#"
                            onClick={(e) => handleServiceClick(index + 1, e)}
                            className="block px-8 py-2 font-semibold text-blue-900 hover:bg-red-50 hover:text-red-600 border-b last:border-none"
                        >
                          {service}
                        </a>
                    ))}
                  </div>
                </div>
              </div>

              {/* À propos */}
              <div
                  className="relative"
                  onMouseEnter={() => handleMouseEnter("about")}
                  onMouseLeave={() => handleMouseLeave("about")}
              >
                <button className="flex items-center text-blue-900 font-semibold hover:text-red-600 transition-all px-4">
                  À propos
                  <ChevronDown
                      className={`ml-1 h-4 w-4 transition-transform ${
                          isAboutOpen ? "rotate-180" : ""
                      }`}
                  />
                </button>
                <div
                    className={`absolute z-[200] mt-3 rounded-md shadow-lg transition-all origin-top ${
                        isAboutOpen
                            ? "opacity-100 scale-100 visible"
                            : "opacity-0 scale-95 hidden"
                    } ${isScrolled ? "navBarStyleScrollingB" : "navC"}`}
                    style={{ left: "50%", transform: "translateX(-50%)" }}
                >
                  <div className="py-1 text-base">
                    <Link
                        to={about}
                        onClick={() => setIsAboutOpen(false)}
                        className="block px-8 py-2 font-semibold text-blue-900 hover:bg-red-50 hover:text-red-600 border-b"
                    >
                      Qui sommes-nous
                    </Link>
                    <Link
                        to={articles}
                        onClick={() => setIsAboutOpen(false)}
                        className="block px-8 py-2 font-semibold text-blue-900 hover:bg-red-50 hover:text-red-600"
                    >
                      Nos articles
                    </Link>
                  </div>
                </div>
              </div>

              {/* Other Pages */}
              <Link
                  to={meetLocation}
                  className={`font-semibold hover:text-red-600 px-4 ${
                      location.pathname === meetLocation ? "active" : "text-blue-900"
                  }`}
              >
                Où nous rencontrer ?
              </Link>
              <Link
                  to={contact}
                  className={`font-semibold hover:text-red-600 px-4 ${
                      location.pathname === contact ? "active" : "text-blue-900"
                  }`}
              >
                Nous contacter
              </Link>
            </div>

            {/* Social Links */}
            <div className="hidden lg:flex space-x-8">
              <a href={facebook} target="_blank" rel="noopener noreferrer">
                <FaFacebook className="text-blue-600 w-6 h-6 hover:text-blue-800" />
              </a>
              <a href={instagram} target="_blank" rel="noopener noreferrer">
                <FaInstagram className="text-pink-600 w-6 h-6 hover:text-pink-800" />
              </a>
              <a href={tiktok} target="_blank" rel="noopener noreferrer">
                <FaTiktok className="text-gray-900 w-6 h-6 hover:text-gray-700"/>
              </a>
              <a href={`tel:${phone}`}>
                <FaPhoneAlt className="text-green-600 w-6 h-6 hover:text-green-800" />
              </a>
            </div>

                    {/* Mobile Menu Toggle (Visible on screens <= 1024px) */}
                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors duration-300 focus:outline-none"
                    >
                        {isMobileMenuOpen ? (
                            <X className="h-8 w-8 text-gray-600 transform rotate-180 transition-transform duration-500"/>
                        ) : (
                            <Menu className="h-8 w-8 text-gray-600 transition-transform duration-500"/>
                        )}
                    </button>
                </div>

                {/* Mobile Menu (Visible on screens <= 1024px) */}
                <div
                    className={`lg:hidden overflow-hidden transition-all duration-500 ease-in-out ${
                        isMobileMenuOpen
                            ? "h-96 opacity-100 visible"
                            : "h-0 opacity-0 invisible"
                    }`}
                >
                    <div className="pb-4 space-y-2">
                        <Link
                            to={`${home}`}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`block px-4 py-2 font-semibold flex items-center ${
                                location.pathname === home ? "active" : "text-blue-900"
                            } hover:bg-red-50 hover:text-red-600 transition-colors duration-200`}
                        >
                            <Home className="w-5 h-5 mr-2"/>
                            Accueil
                        </Link>

                        {/* Mobile Services Dropdown */}
                        <button
                            onClick={() => setIsServicesOpen(!isServicesOpen)}
                            className="w-full px-4 py-2 font-semibold text-left text-blue-900 hover:bg-red-50 hover:text-red-600 transition-colors duration-200 flex items-center justify-between focus:outline-none"
                        >
                            <div className="flex items-center">
                                <Briefcase className="w-5 h-5 mr-2"/>
                                Nos procédures
                            </div>
                            <ChevronDown
                                className={`h-4 w-4 transform transition-transform duration-500 ${
                                    isServicesOpen ? "rotate-180" : ""
                                }`}
                            />
                        </button>

                        <div
                            className={`space-y-1 transition-all duration-500 ${
                                isServicesOpen ? "block" : "hidden"
                            }`}
                        >
                            {[
                                "Naturalisation",
                                "Titre de séjour",
                                "Dossier bloqué en préfecture",
                                "Accélération des procédures",
                                "Regroupement familial",
                                "OQTF",
                            ].map((service, index) => (
                                <a
                                    key={index}
                                    className="block px-8 py-2 font-semibold text-blue-900 hover:bg-red-50 hover:text-red-600 transition-colors duration-200"
                                    onClick={(e) => handleServiceClick(index + 1, e)}
                                >
                                    {service}
                                </a>
                            ))}
                        </div>

                        {/* Mobile À propos Dropdown */}
                        <button
                            onClick={() => setIsMobileAboutOpen(!isMobileAboutOpen)}
                            className="w-full px-4 py-2 font-semibold text-left text-blue-900 hover:bg-red-50 hover:text-red-600 transition-colors duration-200 flex items-center justify-between focus:outline-none"
                        >
                            <div className="flex items-center">
                                <Info className="w-5 h-5 mr-2"/>
                                À propos
                            </div>
                            <ChevronDown
                                className={`h-4 w-4 transform transition-transform duration-500 ${
                                    isMobileAboutOpen ? "rotate-180" : ""
                                }`}
                            />
                        </button>

                        <div
                            className={`space-y-1 transition-all duration-500 ${
                                isMobileAboutOpen ? "block" : "hidden"
                            }`}
                        >
                            <Link
                                to={`${about}`}
                                onClick={() => {
                                    setIsMobileAboutOpen(false);
                                    setIsMobileMenuOpen(false);
                                }}
                                className={`block px-8 py-2 font-semibold text-blue-900 hover:bg-red-50 hover:text-red-600 transition-colors duration-200 ${
                                    location.pathname === about ? "active" : "text-blue-900"
                                }`}
                            >
                                Qui sommes-nous
                            </Link>
                            <Link
                                to={`${articles}`}
                                onClick={() => {
                                    setIsMobileAboutOpen(false);
                                    setIsMobileMenuOpen(false);
                                }}
                                className={`block px-8 py-2 font-semibold text-blue-900 hover:bg-red-50 hover:text-red-600 transition-colors duration-200 ${
                                    location.pathname === articles ? "active" : "text-blue-900"
                                }`}
                            >
                                Nos articles
                            </Link>
                        </div>

                        <Link
                            to={`${meetLocation}`}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`block px-4 py-2 font-semibold flex items-center hover:bg-red-50 hover:text-red-600 transition-colors duration-200 ${
                                location.pathname === meetLocation ? "active" : "text-blue-900"
                            } `}
                        >
                            <MapPin className="w-5 h-5 mr-2"/>
                            Où nous rencontrer ?
                        </Link>
                        <Link
                            to={`${contact}`}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`block px-4 py-2 font-semibold flex items-center hover:bg-red-50 hover:text-red-600 transition-colors duration-200 ${
                                location.pathname === contact ? "active" : "text-blue-900"
                            } `}
                        >
                            <Mail className="w-5 h-5 mr-2"/>
                            Nous contacter
                        </Link>

                        {/* Mobile Contact Button */}
                        <div className="px-4 pt-3">
                            <div className="  space-x-8 flex justify-center">
                                <a href={facebook} target="_blank" rel="noopener noreferrer">
                                    <FaFacebook className="text-blue-600 w-6 h-6 hover:text-blue-800"/>
                                </a>

                                <a href={instagram} target="_blank" rel="noopener noreferrer">
                                    <FaInstagram className="text-pink-600 w-6 h-6 hover:text-pink-800"/>
                                </a>
                                <a href={tiktok} target="_blank" rel="noopener noreferrer">
                                    <FaTiktok className="text-black w-6 h-6 hover:text-gray-800"/>
                                </a>
                                <a href={`tel:${phone}`}>
                                    <FaPhoneAlt className="text-green-600 w-6 h-6 hover:text-green-800"/>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </nav>
        </div>
    );
};

export default Navbar;